import { Icons } from "assets";
import classNames from "classnames";
import { useFormik } from "formik";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import CustomSelect from "shared/components/common/customSelect";
import { JobRole } from "shared/utils/enum";
import { QuestionnaireStep1VS } from "shared/utils/validation";
import styles from "./style.module.scss";
import { useState } from "react";
import SuccessModal from "shared/modal/success";
import {
  AddEvaluationRequestService,
  CheckJobCodeService,
} from "shared/services/evaluations";
import { toastMessage } from "shared/components/common/toast";
import { useSelector } from "react-redux";
import useDebounce from "shared/hooks/useDebounce";

interface formikValuesInterface {
  jobs: RequestType[];
}

const RequestEvaluations = () => {
  const {
    theme: { theme },
  } = useSelector((state: any) => state?.root);

  const initialValues: formikValuesInterface = {
    jobs: [
      {
        jobTitle: "",
        department: "",
        jobCode: "",
        jobGrade: "",
        roleType: null,
      },
    ],
  };

  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [jobCodeError, setJobCodeError] = useState<any[]>([
    {
      message: "",
      error: false,
    },
  ]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: QuestionnaireStep1VS,
    onSubmit: (value, action) => {
      setSubmitting(true);
      handleAddRequest();
    },
  });

  const {
    handleChange,
    handleSubmit,
    values,
    touched,
    errors,
    setFieldValue,
    setSubmitting,
    isSubmitting,
    resetForm,
    setFieldError,
  } = formik;

  const handleAddRequest = () => {
    if (jobCodeError.every((item) => !item.error)) {
      let tempArr = [];
      for (let i = 0; i < formik.values.jobs?.length; i++) {
        let job = formik.values.jobs[i];
        let obj: any = {
          jobTitle: job?.jobTitle,
          department: job?.department,
          roleType: job?.roleType?.value,
        };
        if (job?.jobCode) {
          obj!.jobCode = job?.jobCode;
        }
        if (job?.jobGrade) {
          obj!.jobGrade = job?.jobGrade;
        }
        tempArr.push(obj);
      }
      let paylaod = {
        requests: tempArr,
      };
      AddEvaluationRequestService(paylaod)
        .then(() => {
          resetForm();
          handleOpenSuccessModal();
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.error);
          let data: any[] = err?.response?.data?.data;
          let errorType = err?.response?.data?.errorType;
          if (errorType === 1) {
            for (let i = 0; i < formik.values.jobs?.length; i++) {
              let job = formik.values.jobs[i];
              if (data.includes(job?.jobCode)) {
                formik.setFieldError(
                  `jobs[${i}].jobCode`,
                  "Duplicate Job Code"
                );
              }
            }
          } else if (errorType === 2) {
            for (let i = 0; i < formik.values.jobs?.length; i++) {
              let job = formik.values.jobs[i];
              if (data.includes(job?.jobCode)) {
                formik.setFieldError(
                  `jobs[${i}].jobCode`,
                  "Job Code Already Exists"
                );
              }
            }
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      setSubmitting(false);
    }
  };

  const handleAddJobForm = () => {
    formik.setFieldValue("jobs", [
      ...values.jobs,
      {
        jobTitle: "",
        department: "",
        jobCode: "",
        jobGrade: "",
        roleType: null,
      },
    ]);
    setJobCodeError([
      ...jobCodeError,
      {
        message: "",
        error: false,
      },
    ]);
  };

  const handleRemoveJobForm = (index: number) => {
    const newJobs = [...values.jobs];
    newJobs.splice(index, 1);
    formik.setFieldValue("jobs", newJobs);
    const newErrors = [...jobCodeError];
    newErrors.splice(index, 1);
    setJobCodeError(newErrors);
  };

  const handleOpenSuccessModal = () => {
    setOpenSuccessModal(true);
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
  };

  return (
    <div className={classNames(styles.mainContainer)}>
      <div>
        <h1>
          Request an <span>Evaluation by CGP</span>
        </h1>
        <p>Fill out the fields for jobs you want evaluated by CGP</p>
      </div>
      <div className={classNames(styles.tableWrapper)}>
        <table className="table" style={{ minWidth: "800px", margin: "0px" }}>
          <thead>
            <tr style={{ height: "44px" }}>
              <th
                className={classNames(styles.tableHead)}
                style={{ width: "20%" }}
              >
                Job Title <span>*</span>
              </th>
              <th
                className={classNames(styles.tableHead)}
                style={{ width: "20%" }}
              >
                Department <span>*</span>
              </th>
              <th
                className={classNames(styles.tableHead)}
                style={{ width: "20%" }}
              >
                Job Code
              </th>
              <th
                className={classNames(styles.tableHead)}
                style={{ width: "20%" }}
              >
                Job Grade
              </th>
              <th
                className={classNames(styles.tableHead)}
                style={{ width: "20%" }}
              >
                Role Type <span>*</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {values.jobs.map((job, index) => {
              return (
                <RowItem
                  key={index}
                  job={job}
                  handleChange={handleChange}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  values={values}
                  handleRemoveJobForm={handleRemoveJobForm}
                  jobCodeError={jobCodeError}
                  setJobCodeError={setJobCodeError}
                  setFieldError={setFieldError}
                  index={index}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      <CustomButton
        title="Add More Roles"
        Icon={Icons.Plus}
        containerStyle={classNames(
          theme === "dark" ? styles.btnStyle : styles.lighThemeBtnStyle,
          "gap-1 mt-3"
        )}
        IconDirction="left"
        onClick={() => {
          handleAddJobForm();
        }}
      />
      <CustomButton
        title="Submit Request"
        containerStyle={classNames("self-end mt-3")}
        loading={isSubmitting}
        disabled={isSubmitting}
        onClick={() => {
          handleSubmit();
        }}
      />
      <SuccessModal
        show={openSuccessModal}
        handleClose={handleCloseSuccessModal}
        title="Your request has been submitted successfully!"
        subtitle="CGP will reach out for additional information required to complete the evaluation."
        actionButtonText="Close"
      />
    </div>
  );
};

const RowItem = ({
  handleChange,
  job,
  index,
  touched,
  setFieldValue,
  errors,
  values,
  handleRemoveJobForm,
  jobCodeError,
  setJobCodeError,
  setFieldError,
}: any) => {
  const options = [
    {
      title: "Management",
      Icon: null,
      action: (index: number) => {
        setFieldValue(`jobs[${index}].roleType`, {
          name: "Management",
          value: JobRole.management,
        });
      },
    },
    {
      title: "Individual Contributor",
      Icon: null,
      action: (index: number) => {
        setFieldValue(`jobs[${index}].roleType`, {
          name: "Individual Contributor",
          value: JobRole.individualContributor,
        });
      },
    },
  ];

  const handleCheckJobCode = () => {
    CheckJobCodeService({ jobCode: job.jobCode ?? "" })
      .then(({ data }) => {
        if (data === 1) {
          setFieldError(
            `jobs.[${index}].jobCode`,
            "This Job Code Already exists"
          );
          let temp = [...jobCodeError];
          temp[index] = {
            message: "This Job Code Already exists",
            error: true,
          };
          setJobCodeError(temp);
        } else {
          let temp = [...jobCodeError];
          temp[index] = {
            message: "",
            error: false,
          };
          setJobCodeError(temp);
        }
      })
      .catch((err) => {
        let temp = [...jobCodeError];
        temp[index] = {
          message: "",
          error: false,
        };
        setJobCodeError(temp);
      });
  };

  useDebounce(
    () => {
      handleCheckJobCode();
    },
    [job.jobCode],
    800
  );

  return (
    <tr className="relative">
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          required
          placeholder="Enter Job Title"
          value={job.jobTitle}
          onChange={handleChange("jobs." + index + ".jobTitle")}
          error={
            touched.jobs?.[index]?.jobTitle &&
            //@ts-ignore
            errors.jobs?.[index]?.jobTitle
          }
          notRequiredMargin
          customInputContainer={classNames(styles.customInputStyle)}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          required
          placeholder="Enter Department"
          value={job.department}
          onChange={handleChange("jobs." + index + ".department")}
          error={
            touched.jobs?.[index]?.department &&
            //@ts-ignore
            errors.jobs?.[index]?.department
          }
          notRequiredMargin
          customInputContainer={classNames(styles.customInputStyle)}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          placeholder="Enter Job Code"
          value={job.jobCode}
          onChange={handleChange("jobs." + index + ".jobCode")}
          error={
            //@ts-ignore
            errors.jobs?.[index]?.jobCode || jobCodeError[index]?.message
          }
          notRequiredMargin
          customInputContainer={classNames(styles.customInputStyle)}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          placeholder="Enter Job Grade"
          value={job.jobGrade}
          onChange={handleChange("jobs." + index + ".jobGrade")}
          error={
            touched.jobs?.[index]?.jobGrade &&
            //@ts-ignore
            errors.jobs?.[index]?.jobGrade
          }
          notRequiredMargin
          customInputContainer={classNames(styles.customInputStyle)}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <div className={classNames("flex items-center gap-2")}>
          <CustomSelect
            placeholder="Select Role Type"
            required
            options={options}
            value={job.roleType?.name}
            error={
              touched.jobs?.[index]?.roleType &&
              //@ts-ignore
              errors.jobs?.[index]?.roleType
            }
            tabIndex={index}
            notRequiredMargin
            customInputContainer={classNames(styles.selectionContainer)}
          />
          {values.jobs?.length > 1 ? (
            <div
              className={classNames(styles.trashIconContainer)}
              onClick={() => {
                if (values.jobs?.length > 1) {
                  handleRemoveJobForm(index);
                }
              }}
            >
              <Icons.Cross />
            </div>
          ) : null}
        </div>
      </td>
    </tr>
  );
};

export default RequestEvaluations;
